import { useCallback, useEffect, useRef, useState } from 'react';

import AgoraRTC from 'agora-rtc-sdk-ng';

import { useAppContext } from '@/app/AppContext';
import Button from "@ergeon/core-components/exports/Button.ts";

import { ArrowPathRoundedSquareIcon } from "@ergeon/icons/hero/outline";
import { EVENTS, gtag } from '@/lib/gtag';

/**
 * Flipping to the rear camera is the whole point of the walkthrough, and the
 * account manager talks the customer into it by colour — "tap the blue button"
 * is how 37% of recorded calls name this control, against three calls in forty
 * thousand that call it the flip button.
 */
export default function ButtonSwitchCamera() {
  const appState = useAppContext();

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);

  // A tap is answered only once the camera has actually swapped. This is the one
  // control that knows which way the camera ends up pointing, so it is the only
  // writer of a known direction — and a refused switch (another app holding
  // the camera, a constraint the device won't meet) must leave that direction
  // alone rather than claim a flip that never happened.
  const isSwitchingRef = useRef(false);

  const toggleFacingMode = useCallback(async () => {
    if (isSwitchingRef.current) {
      return;
    }
    isSwitchingRef.current = true;
    // Flips the recorded direction, never one read back from getSettings():
    // mobile Safari reports 'environment' for a front camera, and believing it
    // un-mirrors the customer's own face — they move right and their reflection
    // goes left. A null record (a camera picked by id in settings) flips to the
    // front: the picker's usual job is choosing among the rear lenses.
    const newFacingMode = appState.cameraFacing === 'user' ? 'environment' : 'user';
    try {
      if (!await appState.camera.changeDevice({ facingMode: newFacingMode })) {
        return;
      }
      appState.setCameraFacing(newFacingMode);

      gtag(EVENTS.CHANGE_DEVICE, { facingMode: newFacingMode });
    } finally {
      isSwitchingRef.current = false;
    }
  }, [appState]);

  useEffect(function initCameras() {
    if (appState.camera.isPermissionGranted) {
      AgoraRTC.getCameras().then(setCameras, () => console.debug('Unable to access cameras'));
    }
  }, [appState.camera]);

  // Nothing to flip between on a laptop with one webcam, and a labelled button
  // that cannot do what it says is worse than no button. A device does not
  // report which way a camera points without opening it, so a second camera is
  // the available stand-in for "front and back" — true of the phones this is
  // for, and the same count the button was greyed out by before.
  if (cameras.length <= 1) {
    return null;
  }

  return (
    <Button
      // Blue on purpose: the AM script tells the customer to "tap the blue
      // button" to flip the camera, so this one has to stay findable by colour.
      flavor="primary"
      onClick={toggleFacingMode}
      shape="circle"
      size="large"
      title="Flip camera">
      <ArrowPathRoundedSquareIcon className="size-6 shrink-0" />
      Flip camera
    </Button>
  );
}
